/**
 * [HF-POST-CHRIS] TabErrorBoundary - draft-room resiliency safety net.
 *
 * Originally introduced to catch tab render throws in TabbedPanel; now
 * generalized so ANY high-blast-radius zone can wrap its children and
 * survive a downstream render exception WITHOUT unmounting the whole
 * draft-room tree (which was killing audio + WS on a single tab crash).
 *
 * Callers pass:
 *   • fallbackLabel: string  - headline text ("Analytics unavailable")
 *   • fallbackHint:  string  - sub-line explanation
 *   • testid:        string  - data-testid for the fallback DOM node
 *   • resetKey:      any     - bumping this clears a previously-caught
 *                              error (typical: active tab id, player id).
 *
 * IMPORTANT: to catch errors from a computed child, callers must wrap
 * the child call in an intermediate component (see TabbedPanel's
 * `TabRenderer`). React boundaries only catch errors thrown while
 * rendering DESCENDANTS, never the parent's own render phase.
 */
import { Component } from "react";

export default class TabErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error(
      `[TabErrorBoundary${this.props.testid ? "/" + this.props.testid : ""}] render failed:`,
      error,
      info?.componentStack,
    );
  }

  componentDidUpdate(prevProps) {
    if (prevProps.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({ error: null });
    }
  }

  render() {
    if (this.state.error) {
      const label = this.props.fallbackLabel || "Tab unavailable";
      const hint =
        this.props.fallbackHint ||
        "This section hit a rendering error. The draft is still running - switch tabs or refresh the page to retry.";
      const testid = this.props.testid || "tab-error-boundary";
      return (
        <div
          className="h-full flex flex-col items-center justify-center px-4 py-6 text-center"
          data-testid={testid}
        >
          <p className="font-display font-black text-sm uppercase tracking-widest text-[#FF6B00] mb-2">
            {label}
          </p>
          <p className="font-body text-xs text-[#A0A0A0] max-w-xs">
            {hint}
          </p>
        </div>
      );
    }
    return this.props.children;
  }
}
